"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Check, Cookie } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAnalyticsConsent } from "@/lib/use-analytics-consent";
import type { AnalyticsConsent } from "@/lib/analytics-consent";
import { isTvKioskPath } from "@/lib/public-routes";

function isPartnerReportPath(pathname: string | null): boolean {
  return pathname === "/reports" || (pathname?.startsWith("/reports/") ?? false);
}

export function CookieConsentBanner() {
  const pathname = usePathname();
  const t = useTranslations("CookieConsent");
  const { shouldShowBanner, accept, reject } = useAnalyticsConsent();

  // Partner reports are documents we send to prospects. Leaving the choice
  // unset keeps PostHog off (it only starts after an explicit accept).
  // The TV kiosk is a shared screen, so the banner stays off there too.
  if (!shouldShowBanner || isPartnerReportPath(pathname) || isTvKioskPath(pathname)) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label={t("title")}
      className="fixed inset-x-0 bottom-0 z-[10050] p-4 pb-[calc(1rem_+_var(--safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(1.5rem_+_var(--safe-area-inset-bottom))]"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-5 py-4 shadow-[0_-12px_40px_rgba(0,0,0,0.45)] sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-5">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-(--mk-gold-border) bg-(--mk-gold-bg)">
            <Cookie className="h-5 w-5 text-(--mk-gold)" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="font-serif text-base font-bold text-(--mk-cream)">
              {t("title")}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-(--mk-cream-75)">
              {t("body")}
            </p>
            <p className="mt-1 flex flex-wrap gap-x-3 text-sm">
              <Link
                href="/privacy"
                className="cursor-pointer font-medium text-(--mk-gold-light) underline-offset-2 hover:underline"
              >
                {t("privacyLink")}
              </Link>
              <Link
                href="/cookies"
                className="cursor-pointer font-medium text-(--mk-gold-light) underline-offset-2 hover:underline"
              >
                {t("cookiesLink")}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => reject("cookie_banner")}
            className="cursor-pointer rounded-lg border border-(--mk-cream-08) bg-transparent px-4 py-2.5 min-h-11 text-sm font-semibold text-(--mk-cream-75) transition-colors hover:bg-(--mk-cream-surface) hover:text-(--mk-cream) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--mk-gold) disabled:cursor-not-allowed"
          >
            {t("reject")}
          </button>
          <button
            type="button"
            onClick={() => accept("cookie_banner")}
            className="cursor-pointer rounded-lg bg-(--mk-gold) px-4 py-2.5 min-h-11 text-sm font-semibold text-(--mk-ink) transition-colors hover:bg-(--mk-gold-light) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--mk-gold) disabled:cursor-not-allowed"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentChoiceButton({
  label,
  selected,
  onSelect,
  variant,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  variant: "primary" | "secondary";
}) {
  const className =
    variant === "primary"
      ? "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-(--mk-gold) px-4 py-2.5 min-h-11 text-sm font-semibold text-(--mk-ink) transition-colors hover:bg-(--mk-gold-light) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--mk-gold) disabled:cursor-not-allowed"
      : "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-(--mk-cream-08) bg-transparent px-4 py-2.5 min-h-11 text-sm font-semibold text-(--mk-cream-75) transition-colors hover:bg-(--mk-cream-surface) hover:text-(--mk-cream) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--mk-gold) disabled:cursor-not-allowed";

  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={selected}
      onClick={() => {
        if (selected) {
          return;
        }
        onSelect();
      }}
      className={className}
    >
      {selected ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
      {label}
    </button>
  );
}

export function CookieConsentSettings() {
  const t = useTranslations("CookieConsent");
  const { consent, isReady, accept, reject } = useAnalyticsConsent();

  if (!isReady) {
    return null;
  }

  const statusKey =
    consent === "accepted"
      ? "statusAccepted"
      : consent === "rejected"
        ? "statusRejected"
        : "statusUnset";

  function choose(next: AnalyticsConsent) {
    if (consent === next) {
      return;
    }
    if (next === "accepted") {
      accept("cookie_settings");
      return;
    }
    reject("cookie_settings");
  }

  return (
    <div className="rounded-2xl border border-(--mk-cream-whisper) bg-(--mk-ink-soft) p-6">
      <h2 className="font-serif text-xl font-bold text-(--mk-cream)">
        {t("settingsTitle")}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-(--mk-cream-75)">
        {t("settingsBody")}
      </p>
      <p
        aria-live="polite"
        className="mt-3 text-sm font-medium text-(--mk-cream-80)"
      >
        {t(statusKey)}
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <ConsentChoiceButton
          label={t("accept")}
          selected={consent === "accepted"}
          onSelect={() => choose("accepted")}
          variant="primary"
        />
        <ConsentChoiceButton
          label={t("reject")}
          selected={consent === "rejected"}
          onSelect={() => choose("rejected")}
          variant="secondary"
        />
      </div>
    </div>
  );
}
